'use client';

import { cloneElement, forwardRef, isValidElement, useContext, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { VerticalNavContext } from './context/vertical-nav.context';
import { verticalNavItemRecipe } from './styles/vertical-nav-item.styles';
import type { VerticalNavItemPropsInterface } from './types/vertical-nav-item.types';

/**
 * @since 8.14.0
 */
export const VerticalNavItem = forwardRef<HTMLLIElement, VerticalNavItemPropsInterface>(
  (props: VerticalNavItemPropsInterface, forwardedRef) => {
    const {
      children,
      className,
      css: cssProp,
      disabled = false,
      current,
      selected,
      ...rest
    } = props;

    const { tone } = useContext(VerticalNavContext);

    const styles = css(
      verticalNavItemRecipe.raw({
        selected,
        current: current === 'true' || current === 'false' ? !!current : current,
        disabled,
        tone,
      }),
      cssProp,
    );

    const a11yDisabledState = useMemo(
      () => ({
        ...(disabled ? { 'aria-disabled': disabled } : null),
      }),
      [disabled],
    );

    const a11ySelectedState = useMemo(
      () => ({
        ...(selected ? { 'aria-selected': selected } : null),
      }),
      [selected],
    );

    const a11yCurrentState = useMemo(
      () => ({
        ...(current ? { 'aria-current': current } : null),
      }),
      [current],
    );

    // Remove the `href` if disabled (CDS-3388)
    const content = useMemo(() => {
      if (disabled && isValidElement(children)) {
        if (children.props.href) {
          return cloneElement(children, {
            'href': undefined,
            'role': 'link',
            'tabIndex': 0,
            'aria-disabled': true,
          } as any);
        }
      }
      return children;
    }, [children, disabled]);

    return (
      <li
        ref={forwardedRef}
        data-c11n-component="VerticalNav.Item"
        data-c11n-version={__C11N_VERSION__}
        className={cx('c11n', styles, className)}
        {...a11yDisabledState}
        {...a11ySelectedState}
        {...a11yCurrentState}
        {...rest}
      >
        {content}
      </li>
    );
  },
);

VerticalNavItem.displayName = 'VerticalNav.Item';
